import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoWordmark } from "./Logo";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY } from "../../i18n/dict";
import { scrollToId } from "./SmoothScroll";

const LANGS = ["fr", "es", "en"];

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "hadjj-umrah", label: t.nav.hadjj },
    { id: "destinations", label: t.nav.destinations },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "contact", label: t.nav.contact },
  ];

  const tone = scrolled ? "dark" : "light";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,color,backdrop-filter,padding] duration-500 ${
        scrolled
          ? "bg-[#F5F1E8]/90 backdrop-blur-xl text-[#0B1416] py-3 border-b border-[#0B1416]/8"
          : "bg-transparent text-[#F5F1E8] py-5"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => scrollToId("top")}
          className="flex items-center"
        >
          <LogoWordmark tone={tone === "light" ? "light" : "dark"} />
        </button>

        <nav className="hidden lg:flex items-center gap-10 text-[13px]" data-testid="desktop-nav">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="nav-link uppercase tracking-[0.22em] font-medium"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div
            data-testid="language-switcher"
            className={`hidden sm:flex items-center gap-1 text-[11px] tracking-[0.28em] uppercase font-medium px-3 py-2 rounded-full border transition-colors ${
              scrolled
                ? "border-[#0B1416]/20"
                : "border-[#F5F1E8]/30"
            }`}
          >
            {LANGS.map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="opacity-40">·</span>}
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-1 transition-opacity ${
                    lang === l ? "opacity-100" : "opacity-45 hover:opacity-80"
                  }`}
                >
                  {l}
                </button>
              </React.Fragment>
            ))}
          </div>

          <button
            data-testid="header-cta-book"
            onClick={() => navigate("/book")}
            className={`tt-btn hidden md:inline-flex ${
              scrolled ? "tt-btn-dark" : "tt-btn-primary"
            }`}
          >
            {t.nav.book}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden bg-[#0B1416] text-[#F5F1E8]"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => { setOpen(false); scrollToId(l.id); }}
                  className="font-heading text-3xl text-left"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex gap-3 mt-4">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    data-testid={`mobile-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1 border rounded-full text-xs uppercase tracking-[0.28em] ${
                      lang === l ? "bg-[#1BB6B9] text-[#0B1416] border-[#1BB6B9]" : "border-[#F5F1E8]/25"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
